// BarChart.js
import moment from 'moment';
import * as d3 from 'd3';
import React, { useState, useEffect, useContext, useRef } from 'react';

import { ItemContext } from '../App';
import { GoogleItemType, PromaBlocks } from './itemDefinitions';
import HierarchyPopover from './HierarchyPopover';
import { PromaPopupContent } from './MapItems';

const blockArray = Object.values(PromaBlocks)

const StreamGraphChart = ({
  height,
  width,
  margin,
  data,
  chartData,
  timeRange,
  eventIds,
  inHeight,
  inWidth,
  setActiveNode,
  setMouseEvent,
  setTargetOffset
}) => {
  const ref = useRef();

  const handleNodeClick = (e, d) => {
    setActiveNode(data.find((item) => item.id === d.key));
    e.preventDefault();
    setMouseEvent(e);
    setTargetOffset({ left: e.clientX, top: e.clientY });
  };

  const draw = () => {
    d3.select(ref.current).selectAll('*').remove();
    const svg = d3
      .select(ref.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    function zoomed({ transform }) {
      g.attr('transform', transform + ',translate(0,' + inHeight * 0.8 + ')');
      path.attr('transform', transform);
    }
    svg.call(d3.zoom().scaleExtent([1, 8]).on('zoom', zoomed));

    // List of groups = header of the csv files
    const keys = eventIds.slice(1);

    // Add X axis
    const x = d3.scaleTime().domain(timeRange).range([0, inWidth]).nice();

    const g = svg.append('g').attr('cursor', 'grab');
    g.attr('transform', 'translate(0,' + inHeight * 0.8 + ')')
      .call(
        d3.axisBottom(x).tickSize(-inHeight * 0.7)
        //.tickValues([1900, 1925, 1975, 2000])
      )
      .select('.domain')
      .remove();
    // Customization
    svg.selectAll('.tick line').attr('stroke', '#b8b8b8');

    // Add X axis label:
    svg
      .append('text')
      .attr('text-anchor', 'end')
      .attr('x', inWidth)
      .attr('y', inHeight - 30)
      .text('Y-akselilla oman henkilöstön vahvuus - zoomaa ja klikkaa');

    // Add Y axis
    const y = d3.scaleLinear().domain([-40, 40]).range([inHeight, 0]);

    // color palette
    const color = d3.scaleOrdinal().domain(blockArray).range(d3.schemeDark2);

    // stack the data?
    const stackedData = d3.stack().offset(d3.stackOffsetSilhouette).keys(keys)(chartData);
    // Three function that change the tooltip when user hover / move / leave a cell
    const mouseover = function (d) {
      d3.selectAll('.myArea').style('opacity', 0.2);
      d3.select(this).style('opacity', 1);
    };
    const mouseleave = function (d) {
      d3.selectAll('.myArea').style('opacity', 1).style('stroke', 'none');
    };

    // Area generator
    const area = d3
      .area()
      .x(function (d) {
        return x(d.data.time);
      })
      .y0(function (d) {
        return y(d[0]);
      })
      .y1(function (d) {
        return y(d[1]);
      });

    svg.append('rect').attr('width', '100%').attr('height', '100%').attr('fill-opacity', '0');

    // Show the areas
    const path = svg.selectAll('mylayers').data(stackedData).enter().append('path');
    path
      .attr('class', 'myArea')
      .style('fill', function (d) {
        return color(data.find((item) => item.id === d.key).block);
      })
      .attr('d', area)
      .on('mouseover', mouseover)
      .on('mouseleave', mouseleave)
      .on('click', handleNodeClick)
      .style('cursor', 'pointer');
  };

  useEffect(() => {
    draw();
  }, [height, width, data, chartData, inHeight, inWidth]);

  return (
    <div className="chart">
      <svg ref={ref} />
    </div>
  );
};

const ResourcingStreamGraphItems = ({ showItems, showTabs, itemProps }) => {
  const [height, setHeight] = useState(window.innerHeight - 100);
  const [width, setWidth] = useState(window.innerWidth);
  const [eventIds, setEventIds] = useState([]);
  const [eventsWithRanges, setEventsWithRanges] = useState([]);
  const [transformedData, setTransformedData] = useState([]);

  const { filterDict, filteredItems, items } = useContext(ItemContext);

  const margin = { top: 20, right: 30, bottom: 0, left: 10 };
  const inWidth = width - margin.left - margin.right;
  const inHeight = height - margin.top - margin.bottom;

  // Handle node clicks
  const [activeNode, setActiveNode] = useState();
  const [mouseEvent, setMouseEvent] = useState();
  const [targetOffset, setTargetOffset] = useState();

  /** Update the size of the graph element - with a short delay to avoid too many renders */
  const updateGraphSize = () => {
    setTimeout(() => {
      if (height !== window.innerHeight - 100) {
        setHeight(window.innerHeight - 100);
      }
      if (width !== window.innerWidth) {
        setWidth(window.innerWidth);
      }
    }, 200);
  };

  window.addEventListener('resize', updateGraphSize);

  let filterRange = [new Date('2021-04-01'), new Date('2024-12-31')];
  useEffect(() => {
    filterRange = [new Date('2021-04-01'), new Date('2024-12-31')]; // filterDict.start;

    const invertTime = (time) => d3.scaleTime().domain(filterRange).range([0, 1000]).invert(time);

    const timeArray = Array.from({ length: 1000 }, (v, k) => k + 1).map((val) => invertTime(val));
    const events = items;
    const currEventIds = events.map((item) => item.id);
    setEventIds(currEventIds);
    const currEventsWithRanges = events.map((item) => ({
      ...item,
      timeRange: [moment(item.startDate).toDate(), moment(item.endDate).toDate()]
    }));
    setEventsWithRanges(currEventsWithRanges);
    setTransformedData(
      timeArray.map((time) => ({
        ...Object.fromEntries(
          currEventsWithRanges.map((item) => {
            return [
              item.id,
              moment(time).isBetween(item.timeRange[0], item.timeRange[1])
                ? item.workforceStrength
                : 0
            ];
          })
        ),
        time
      }))
    );
  }, [items, filterDict.start]);

  const chartProps = {
    height,
    width,
    margin,
    inHeight,
    inWidth,
    data: eventsWithRanges,
    chartData: transformedData,
    timeRange: filterRange,
    eventIds,
    setActiveNode,
    setMouseEvent,
    setTargetOffset
  };

  return (
    <div>
      <HierarchyPopover
        content={
          activeNode ? (
            <PromaPopupContent
              item={activeNode}
              filteredItems={filteredItems}
              noChildren
              blockInTitle
            />
          ) : (
            ''
          )
        }
        node={activeNode}
        mouseEvent={mouseEvent}
        setMouseEvent={setMouseEvent}
        targetOffset={targetOffset}
      />
      <StreamGraphChart {...chartProps} />
    </div>
  );
};

export default ResourcingStreamGraphItems;
